import { Component, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';

export class RouteErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null; info: string }> {
  state = { error: null, info: '' };

  static getDerivedStateFromError(error: Error) { return { error }; }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Chunk load failure = stale cache after a new deployment — hard reload to pick up new assets
    if (error.message.includes('Failed to fetch dynamically imported module') ||
        error.message.includes('Importing a module script failed')) {
      window.location.reload();
      return;
    }
    // Log full stack to console so devtools can pinpoint the source
    console.error('[RouteErrorBoundary] Caught error:', error);
    console.error('[RouteErrorBoundary] Component stack:', info.componentStack);
    // Report to Sentry with component stack as context
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack } },
    });
    this.setState({ info: info.componentStack ?? '' });
  }

  render() {
    if (this.state.error) {
      const err = this.state.error as Error;
      return (
        <div className="min-h-screen flex items-center justify-center p-6">
          <div className="max-w-md w-full text-center space-y-4">
            <h2 className="text-xl font-semibold text-destructive">Something went wrong</h2>
            <p className="text-sm text-muted-foreground">
              {err.message}
            </p>
            <details className="text-left">
              <summary className="text-xs text-muted-foreground cursor-pointer">Details (share with dev)</summary>
              <pre className="text-[10px] text-muted-foreground mt-2 whitespace-pre-wrap break-all bg-muted p-2 rounded max-h-40 overflow-y-auto">
                {err.stack}{'\n\nComponent stack:'}{this.state.info}
              </pre>
            </details>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => this.setState({ error: null })}
                className="px-4 py-2 text-sm rounded-md border border-border hover:bg-muted transition-colors"
              >
                Try again
              </button>
              <a
                href="/maintenance"
                className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                onClick={() => this.setState({ error: null })}
              >
                Go home
              </a>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
